// GW2 Wiki service
// Responsible for fetching item images (renders/icons) from the
// official Guild Wars 2 wiki via the MediaWiki JSON API.

// MediaWiki API base (CORS-enabled). We'll add action/props per request.
const WIKI_API_BASE =
	"https://wiki.guildwars2.com/api.php?format=json&origin=*";

// List of common weapon names that need disambiguation on the wiki
const COMMON_WEAPON_NAMES = [
	"Rage",
	"Venom",
	"Spark",
	"Dragon's Claw",
];

const normalizeBaseName = (name) => name.replace(/ Skin$/i, "").trim();

const pickBestImageFromList = (images, baseName) => {
	if (!images || images.length === 0) return null;
	const normalizedBase = baseName
		? baseName.replace(/\s+/g, "_").toLowerCase()
		: "";

	const score = (title) => {
		let s = 0;
		const lower = title.toLowerCase();
		if (/\.jpe?g$/.test(lower)) s += 3; // prefer renders
		if (normalizedBase && lower.includes(normalizedBase)) s += 4; // prefer matching weapon name
		return s;
	};

	const sorted = [...images].sort((a, b) => score(b.title) - score(a.title));
	return sorted[0] || null;
};

const fetchImageForTitle = async (pageTitle, baseNameForScoring) => {
	const encodedTitle = encodeURIComponent(pageTitle);
	const imagesUrl = `${WIKI_API_BASE}&action=query&prop=images&imlimit=max&titles=${encodedTitle}`;

	try {
		const res = await fetch(imagesUrl);
		if (!res.ok) {
			console.error("Wiki images request failed:", res.status, pageTitle);
			return null;
		}

		const data = await res.json();
		const pages = data?.query?.pages;
		if (!pages) return null;

		let allImages = [];
		for (const key in pages) {
			if (!Object.prototype.hasOwnProperty.call(pages, key)) continue;
			const imgs = pages[key]?.images;
			if (Array.isArray(imgs)) allImages = allImages.concat(imgs);
		}

		if (!allImages.length) return null;

		const best = pickBestImageFromList(allImages, baseNameForScoring);
		if (!best || !best.title) return null;

		const encodedFileTitle = encodeURIComponent(best.title);
		const infoUrl = `${WIKI_API_BASE}&action=query&prop=imageinfo&iiprop=url&iiurlwidth=300&titles=${encodedFileTitle}`;

		const infoRes = await fetch(infoUrl);
		if (!infoRes.ok) {
			console.error(
				"Wiki imageinfo request failed:",
				infoRes.status,
				best.title
			);
			return null;
		}

		const infoData = await infoRes.json();
		const infoPages = infoData?.query?.pages;
		if (!infoPages) return null;

		for (const k in infoPages) {
			if (!Object.prototype.hasOwnProperty.call(infoPages, k)) continue;
			const ii = infoPages[k]?.imageinfo;
			if (Array.isArray(ii) && ii[0]) {
				// Prefer thumbnail url when available; fall back to full url
				return ii[0].thumburl || ii[0].url || null;
			}
		}

		return null;
	} catch (err) {
		console.error("Wiki API fetch failed:", pageTitle, err.message || err);
		return null;
	}
};

/**
 * Fetch a suitable wiki image URL for a given item.
 *
 * Expects an object with at least a `name` property.
 * Returns a string URL or null if no appropriate image was found.
 */
export const fetchWikiImageForItem = async (item) => {
	if (!item || !item.name) return null;

	const rawName = item.name;
	const baseName = normalizeBaseName(rawName);

	const candidateTitles = [];

	// For short, ambiguous weapon names like "Rage", prefer the explicit weapon page
	const weaponBase = baseName || rawName;
	if (COMMON_WEAPON_NAMES.includes(weaponBase)) {
		candidateTitles.push(`${weaponBase} (weapon)`);
	}

	candidateTitles.push(rawName);
	if (baseName && baseName !== rawName) {
		candidateTitles.push(baseName);
	}

	const seen = new Set();
	const uniqueTitles = candidateTitles.filter((t) => {
		if (!t) return false;
		const key = t.toLowerCase();
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	});

	let src = null;
	for (const title of uniqueTitles) {
		src = await fetchImageForTitle(title, baseName || rawName);
		if (src) break;
	}

	return src || null;
};

const gw2WikiService = {
	fetchWikiImageForItem,
};

export default gw2WikiService;

